import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Checkbox, Group} from '@mantine/core';
import React, {useState} from 'react';

import {TaskSchemaNodeRowData} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import FieldDefinitionInputOrDisplay from 'registry/field/FieldDefinitionInputOrDisplay';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaField, LeafFieldOption} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import BaseEditableField from 'ui-library/BaseEditableField';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import EditableTextField from 'ui-library/inputs/EditableTextField';

import {useStyles} from './transitionFieldInfoStyles';
import {isLinkFieldType} from './transitionFieldInfoUtils';

export function FieldDisplay(props: {
  field: {fieldIcon: BenchlingIconName; fieldName: string | JSX.Element};
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.fieldDisplay}>
      <BenchlingIcon icon={props.field.fieldIcon} />
      {props.field.fieldName}
    </div>
  );
}

function EditableDefinitionField(props: {
  selectedField: TaskSchemaNodeRowData;
  schemaField: SchemaField;
  availableFieldOptions: Array<SchemaFieldConstants.FieldOption>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  onSelectFieldOption: (fieldOption: LeafFieldOption) => void;
  canEdit: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <div className={classes.editableDefinitionEditing}>
        <FieldDefinitionInputOrDisplay
          availableFieldOptions={props.availableFieldOptions}
          schemaFieldSelectors={props.schemaFieldSelectors}
          currentFieldState={props.schemaField}
          inEditMode={true}
          onSelectFieldOption={(fieldOption: LeafFieldOption) => {
            props.onSelectFieldOption(fieldOption);
            setIsEditing(false);
          }}
          isFieldBeingCreated={false}
        />
        <ActionIcon
          size="sm"
          variant="subtle"
          color="gray"
          onClick={() => {
            setIsEditing(false);
          }}
          aria-label="Cancel"
          className={classes.cancelButton}
          {...getTestProps({component: 'FieldActionButtons', element: 'cancel'})}
        >
          <BenchlingIcon icon="close" className="metadata-action-icon--close" />
        </ActionIcon>
      </div>
    );
  }

  return (
    <BaseEditableField
      isBeingEdited={false}
      isWritable={props.canEdit}
      isCompleteEnabled={false}
      viewer={
        <div className={classes.definitionViewerContent}>
          <FieldDisplay
            field={{fieldIcon: props.selectedField.fieldIcon, fieldName: props.selectedField.fieldTypeLabel}}
          />
        </div>
      }
      editor={null}
      onEdit={() => {
        setIsEditing(true);
      }}
      onCancel={() => {}}
      onComplete={() => {}}
    />
  );
}

export function FieldDescription(props: {
  selectedField: TaskSchemaNodeRowData;
  nodeName: string;
  onClickTask: () => void;
  schemaField: SchemaField;
  onFieldUpdate: (updatedField: SchemaField) => Promise<void>;
  availableFieldOptions: Array<SchemaFieldConstants.FieldOption>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  locked: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const canEdit = !props.locked;
  const canEditDefinition = canEdit && !props.schemaField.isSnapshot;
  const canEditMulti = canEdit && isLinkFieldType(props.schemaField);

  return (
    <>
      <div className={classes.sectionHeading}>Field details</div>
      <div className={classes.fieldMetadataRow}>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'name'})}>
          <div className={classes.fieldMetadataLabel}>Name</div>
          <EditableTextField
            value={props.selectedField.fieldName}
            isDisabled={!canEdit}
            requestChange={(newName) => {
              const name = String(newName);
              if (name !== props.schemaField.name) {
                props.onFieldUpdate({...props.schemaField, name, displayName: name});
              }
            }}
          />
        </div>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'definition'})}>
          <div className={classes.fieldMetadataLabel}>Definition</div>
          <EditableDefinitionField
            selectedField={props.selectedField}
            schemaField={props.schemaField}
            availableFieldOptions={props.availableFieldOptions}
            schemaFieldSelectors={props.schemaFieldSelectors}
            onSelectFieldOption={(fieldOption) => {
              // Snapshot option is disabled so fieldDefinition is always a FieldType here
              const fieldType = fieldOption.fieldDefinition as unknown as SchemaFieldConstants.FieldType;
              props.onFieldUpdate({
                ...props.schemaField,
                requiredLink: fieldOption.requiredLink,
                schemaFieldSelectorId: fieldOption.schemaFieldSelectorId,
                isSnapshot: false,
                fieldType,
                isMulti: isLinkFieldType({...props.schemaField, isSnapshot: false, fieldType})
                  ? props.schemaField.isMulti
                  : false,
              });
            }}
            canEdit={canEditDefinition}
          />
        </div>
        <div {...getTestProps({component: 'TransitionFieldInfoSection', element: 'task'})}>
          <div className={classes.fieldMetadataLabel}>Task</div>
          <Button buttonStyle={ButtonStyle.LINK} onClick={props.onClickTask} text={props.nodeName} />
        </div>
      </div>
      {canEdit && (
        <Group spacing="md" mt="sm">
          <Checkbox
            size="sm"
            checked={props.schemaField.isRequired}
            label="Required"
            onChange={() =>
              props.onFieldUpdate({...props.schemaField, isRequired: !props.schemaField.isRequired})
            }
            disabled={!canEditDefinition}
          />
          <Checkbox
            size="sm"
            checked={props.schemaField.isMulti}
            label="Multi-select"
            onChange={() => props.onFieldUpdate({...props.schemaField, isMulti: !props.schemaField.isMulti})}
            disabled={!canEditMulti}
          />
        </Group>
      )}
    </>
  );
}
